import {
  createApp,
  defineComponent,
  h,
  nextTick,
  onMounted,
  ref,
  watch,
  type App,
  type PropType
} from 'vue';
import { ActionSheet, Popup, showConfirmDialog, showToast } from 'vant';

type Callback<T> = (value: T) => void;

const ScrollView = defineComponent({
  name: 'ScrollViewCompat',
  props: {
    scrollX: Boolean,
    scrollY: Boolean,
    scrollTop: { type: [Number, String], default: 0 },
    scrollLeft: { type: [Number, String], default: 0 },
    showScrollbar: { type: Boolean, default: true }
  },
  emits: ['scroll'],
  setup(props, { emit, expose, slots, attrs }) {
    const element = ref<HTMLElement>();
    const syncPosition = () => {
      const node = element.value;
      if (!node) return;
      const top = Number(props.scrollTop);
      const left = Number(props.scrollLeft);
      if (Number.isFinite(top) && node.scrollTop !== top) node.scrollTop = top;
      if (Number.isFinite(left) && node.scrollLeft !== left) node.scrollLeft = left;
    };
    watch(() => [props.scrollTop, props.scrollLeft], () => void nextTick(syncPosition));
    onMounted(syncPosition);
    expose({ $getMain: () => element.value });
    return () => h('div', {
      ...attrs,
      ref: element,
      style: [attrs.style, {
        overflowX: props.scrollX ? 'auto' : 'hidden',
        overflowY: props.scrollY ? 'auto' : 'hidden',
        scrollbarWidth: props.showScrollbar ? undefined : 'none'
      }],
      onScroll: (event: Event) => {
        const target = event.currentTarget as HTMLElement;
        emit('scroll', {
          detail: { scrollTop: target.scrollTop, scrollLeft: target.scrollLeft }
        });
      }
    }, slots.default?.());
  }
});

const Picker = defineComponent({
  name: 'PickerCompat',
  props: {
    range: { type: Array as PropType<unknown[]>, default: () => [] },
    value: { type: [Number, String], default: 0 },
    disabled: Boolean
  },
  emits: ['change'],
  setup(props, { emit, slots, attrs }) {
    return () => h('label', {
      ...attrs,
      style: [attrs.style, { display: 'block', position: 'relative' }]
    }, [
      slots.default?.(),
      h('select', {
        'aria-label': attrs['aria-label'] || '请选择',
        disabled: props.disabled,
        value: String(props.value),
        style: {
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          opacity: 0,
          cursor: props.disabled ? 'not-allowed' : 'pointer'
        },
        onChange: (event: Event) => {
          const value = Number((event.currentTarget as HTMLSelectElement).value);
          emit('change', { detail: { value } });
        }
      }, props.range.map((label, index) => h('option', { value: index }, String(label))))
    ]);
  }
});

const Slider = defineComponent({
  name: 'SliderCompat',
  props: {
    min: { type: [Number, String], default: 0 },
    max: { type: [Number, String], default: 100 },
    value: { type: [Number, String], default: 0 },
    activeColor: { type: String, default: '#1f6ef5' },
    backgroundColor: { type: String, default: '#e5e7eb' },
    blockColor: { type: String, default: '#1f6ef5' },
    blockSize: { type: [Number, String], default: 18 }
  },
  emits: ['changing', 'change'],
  setup(props, { emit, attrs }) {
    const payload = (event: Event) => ({
      detail: { value: Number((event.currentTarget as HTMLInputElement).value) }
    });
    return () => h('input', {
      ...attrs,
      type: 'range',
      min: props.min,
      max: props.max,
      value: props.value,
      style: [attrs.style, {
        width: '100%',
        accentColor: props.blockColor || props.activeColor,
        background: props.backgroundColor
      }],
      onInput: (event: Event) => emit('changing', payload(event)),
      onChange: (event: Event) => emit('change', payload(event))
    });
  }
});

const UpPopup = defineComponent({
  name: 'UpPopupCompat',
  props: {
    show: Boolean,
    mode: { type: String, default: 'bottom' },
    round: { type: [Boolean, Number, String], default: false },
    safeAreaInsetBottom: Boolean
  },
  emits: ['close'],
  setup(props, { emit, slots }) {
    return () => h(Popup, {
      show: props.show,
      position: (props.mode === 'bottom' ? 'bottom' : props.mode) as any,
      round: Boolean(props.round),
      safeAreaInsetBottom: props.safeAreaInsetBottom,
      onClose: () => emit('close')
    }, slots);
  }
});

interface QueryEntry {
  selector?: string;
  all?: boolean;
  viewport?: boolean;
  callback?: Callback<unknown>;
}

function createSelectorQuery() {
  const entries: QueryEntry[] = [];
  let current: QueryEntry | undefined;
  const query = {
    in: () => query,
    select(selector: string) {
      current = { selector };
      entries.push(current);
      return query;
    },
    selectAll(selector: string) {
      current = { selector, all: true };
      entries.push(current);
      return query;
    },
    selectViewport() {
      current = { viewport: true };
      entries.push(current);
      return query;
    },
    boundingClientRect(callback?: Callback<unknown>) {
      if (current) current.callback = callback;
      return query;
    },
    fields(_options: object, callback?: Callback<unknown>) {
      if (current) current.callback = callback;
      return query;
    },
    scrollOffset(callback?: Callback<unknown>) {
      if (current) current.callback = callback;
      return query;
    },
    exec(callback?: Callback<unknown[]>) {
      const values = entries.map((entry) => {
        if (entry.viewport) {
          return { scrollTop: window.scrollY, scrollLeft: window.scrollX };
        }
        const nodes: Element[] = entry.all
          ? Array.from(document.querySelectorAll(entry.selector || ''))
          : [document.querySelector(entry.selector || '')].filter((node): node is Element => Boolean(node));
        const mapNode = (node: Element) => {
          const rect = node.getBoundingClientRect();
          const scroll = node as HTMLElement;
          return {
            top: rect.top,
            right: rect.right,
            bottom: rect.bottom,
            left: rect.left,
            width: rect.width,
            height: rect.height,
            scrollTop: scroll.scrollTop || 0,
            scrollLeft: scroll.scrollLeft || 0
          };
        };
        const value = entry.all ? nodes.map(mapNode) : nodes[0] ? mapNode(nodes[0]) : null;
        entry.callback?.(value);
        return value;
      });
      callback?.(values);
      return query;
    }
  };
  return query;
}

/** 提供迁移组件仍在使用的少量 uni API；权限和请求仍由真实服务端处理。 */
export function installProposalCompat(app: App): void {
  app.component('scroll-view', ScrollView);
  app.component('picker', Picker);
  app.component('slider', Slider);
  app.component('up-popup', UpPopup);

  const browserUni = {
    getStorageSync: (key: string) => localStorage.getItem(key) || '',
    setStorageSync: (key: string, value: unknown) => localStorage.setItem(key, String(value ?? '')),
    removeStorageSync: (key: string) => localStorage.removeItem(key),
    showToast: ({ title, duration }: { title: string; duration?: number }) =>
      showToast({ message: title, duration }),
    async showModal(options: {
      title?: string;
      content?: string;
      confirmText?: string;
      success?: Callback<{ confirm: boolean; cancel: boolean }>;
    }) {
      try {
        await showConfirmDialog({
          title: options.title,
          message: options.content,
          confirmButtonText: options.confirmText
        });
        const result = { confirm: true, cancel: false };
        options.success?.(result);
        return result;
      } catch {
        const result = { confirm: false, cancel: true };
        options.success?.(result);
        return result;
      }
    },
    async showActionSheet(options: {
      itemList: string[];
      success?: Callback<{ tapIndex: number }>;
    }) {
      const tapIndex = await new Promise<number>((resolve) => {
        const host = document.createElement('div');
        document.body.appendChild(host);
        const visible = ref(true);
        const close = (index: number) => {
          visible.value = false;
          window.setTimeout(() => { sheet.unmount(); host.remove(); resolve(index); }, 0);
        };
        const sheet = createApp(defineComponent({
          setup: () => () => h(ActionSheet, {
            show: visible.value,
            actions: options.itemList.map((name, index) => ({ name, index })),
            cancelText: '取消',
            closeOnClickAction: true,
            onSelect: (action: { index?: number }) => close(Number(action.index)),
            onCancel: () => close(-1)
          })
        }));
        sheet.mount(host);
      });
      const result = { tapIndex };
      if (tapIndex >= 0) options.success?.(result);
      return result;
    },
    async setClipboardData({ data }: { data: string }) {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(data);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = data;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        textarea.remove();
      }
      showToast('链接已复制');
    },
    pageScrollTo: ({ scrollTop, duration }: { scrollTop: number; duration?: number }) =>
      window.scrollTo({ top: scrollTop, behavior: duration ? 'smooth' : 'auto' }),
    createSelectorQuery,
    navigateBack: () => window.history.back(),
    navigateTo: ({ url }: { url: string }) => window.location.assign(url),
    share: ({ fail }: { fail?: Callback<Error> }) => fail?.(new Error('H5 不支持原生分享'))
  };
  Object.assign(globalThis, { uni: browserUni });
}
